/**
 * Phaser-shell ARIA tablist for the dossier (DR-1 + SR-11, 2026-05-13).
 *
 * Wires the DOM + ARIA + keyboard listener to the pure reducer in
 * `dossierTabStripLogic.ts`. Mirrors `dialogueOverlay.ts`'s shell role
 * — ALL state mutation flows through the pure reducer; this module
 * only owns DOM mount + event delegation + ARIA attribute updates.
 *
 * Dual-label rendering for SR-11: each tab button hosts a full-form
 * span ("About") AND an abbreviated span ("ABT"). CSS @media at 360 px
 * toggles which is visible. Caller supplies both via labelFor +
 * labelAbbrevFor — empirical abbreviations like "ABT" don't match a
 * naive slice(0,3) so the caller owns the lookup.
 *
 * Caller lifecycle: `new DossierTabStrip({...})` mounts; the returned
 * instance exposes `getElement()` for parenting + `setActive(tab)` for
 * external programmatic switches + `destroy()` for cleanup. The
 * DossierTabStrip does NOT own the SHUTDOWN handler — FullSheet's
 * destroy() removes its root DOM, which removes this strip transitively.
 */

import {
  reduceTabKey,
  validateTabSwitch,
  type TabKey,
} from './dossierTabStripLogic';

const HANDLED_KEYS: ReadonlySet<string> = new Set([
  'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', ' ',
]);

export interface DossierTabStripOptions<T extends string> {
  /** Visible tabs in caller-determined render order. */
  visibleTabs: readonly T[];
  /** Tab active at mount time. MUST be present in visibleTabs (defensive
   *  fallback to visibleTabs[0] if mismatched — should not occur). */
  initialTab: T;
  /** Tab → full label ("About", "Stats"). Required. */
  labelFor: (tab: T) => string;
  /** Tab → abbreviated label ("ABT", "STA") for ≤360 px viewports
   *  (SR-11). Falls back to `labelFor(...).slice(0, 3).toUpperCase()`
   *  when omitted. */
  labelAbbrevFor?: (tab: T) => string;
  /** Fired AFTER the active tab changes (click + keyboard). The strip
   *  has already updated its own ARIA state by this point — the callback
   *  is for the consuming surface to swap tab body content. */
  onTabChange: (tab: T) => void;
}

export class DossierTabStrip<T extends string> {
  private root: HTMLElement;
  private buttons: Map<T, HTMLButtonElement> = new Map();
  private active: T;
  private readonly opts: DossierTabStripOptions<T>;

  constructor(opts: DossierTabStripOptions<T>) {
    this.opts = opts;
    this.active = opts.visibleTabs.includes(opts.initialTab)
      ? opts.initialTab
      : (opts.visibleTabs[0] ?? opts.initialTab);
    this.root = this.render();
  }

  /** Element for the caller to parent into FullSheet's content. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Currently active tab id. */
  getActive(): T {
    return this.active;
  }

  /**
   * External programmatic switch. Validates `tab` is in visibleTabs;
   * no-op when invalid OR when tab is already active. Fires
   * `onTabChange` only when the active tab actually moves.
   */
  setActive(tab: T): void {
    const next = validateTabSwitch(tab, this.active, this.opts.visibleTabs);
    if (next === this.active) return;
    this.active = next;
    this.updateAriaState();
    this.opts.onTabChange(next);
  }

  /** Tear down DOM. Optional — FullSheet's root.remove() covers this
   *  transitively. Provided for surfaces that scope the strip more tightly. */
  destroy(): void {
    this.root.remove();
    this.buttons.clear();
  }

  private render(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'dossier-tab-strip';
    root.setAttribute('role', 'tablist');

    for (const tab of this.opts.visibleTabs) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dossier-tab-strip__button';
      button.setAttribute('role', 'tab');
      button.setAttribute('data-tab', tab);
      button.setAttribute('aria-selected', tab === this.active ? 'true' : 'false');
      button.tabIndex = tab === this.active ? 0 : -1;

      const fullLabel = this.opts.labelFor(tab);
      const abbrevLabel = this.opts.labelAbbrevFor
        ? this.opts.labelAbbrevFor(tab)
        : fullLabel.slice(0, 3).toUpperCase();

      const fullSpan = document.createElement('span');
      fullSpan.className = 'dossier-tab-strip__label-full';
      fullSpan.textContent = fullLabel;
      button.appendChild(fullSpan);

      const abbrevSpan = document.createElement('span');
      abbrevSpan.className = 'dossier-tab-strip__label-abbrev';
      abbrevSpan.setAttribute('aria-hidden', 'true');
      abbrevSpan.textContent = abbrevLabel;
      button.appendChild(abbrevSpan);

      button.addEventListener('click', () => this.setActive(tab));
      this.buttons.set(tab, button);
      root.appendChild(button);
    }

    root.addEventListener('keydown', (e) => this.onKeyDown(e));
    return root;
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!HANDLED_KEYS.has(e.key)) return;
    e.preventDefault();
    const next = reduceTabKey(
      this.active,
      e.key as TabKey,
      this.opts.visibleTabs,
    );
    if (next !== this.active) this.setActive(next);
    // Move focus to the now-active tab so keyboard nav stays navigable.
    this.buttons.get(this.active)?.focus();
  }

  private updateAriaState(): void {
    for (const [tab, button] of this.buttons) {
      const selected = tab === this.active;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.tabIndex = selected ? 0 : -1;
    }
  }
}
